/**
 * Data hooks for the session drawer tabs: live from the hub, or demo data with ?mock=1.
 *
 *   diff      GET /api/sessions/:id/diff     on open, on Refresh, and every 20 s while the session works
 *   events    GET /api/sessions/:id/events   on open, then only new or changed events every 4 s while live
 *   messages  GET/POST /api/sessions/:id/messages, live through state.ext['session-control']
 *   claims    POST/DELETE /api/sessions/:id/claim
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { isSummary } from '@orgus/protocol';
import type {
  DiffResult,
  DiffScope,
  Session,
  SessionClaim,
  SessionControlState,
  SessionEvent,
  SessionEventsPage,
  SessionInputStatus,
  SessionMessage,
  SessionMessagesResponse,
} from '@orgus/protocol';
import { ApiError, api, apiGet, apiPost, toUiError } from '../../lib/api.ts';
import type { UiError } from '../../lib/api.ts';
import { useOrgus, useExt } from '../../lib/store.ts';
import { demoCloseInput, demoDiff, demoEvents, demoInput, demoSend, seedDemoMessages, useDemoControl } from './demo.ts';
import { eventsQuery, mergeEvents, mergeMessages, replacesEvents, unseenClipped } from './model.ts';

export const DIFF_REFRESH_MS = 20_000;
export const EVENTS_REFRESH_MS = 4_000;

const useMode = () => useOrgus((s) => s.mode);
const enc = encodeURIComponent;

export interface Loadable<T> {
  data: T | null;
  /** First load in flight. */
  loading: boolean;
  /** A reload in flight while data is shown. */
  refreshing: boolean;
  error: UiError | null;
  reload: () => void;
}

// ---------------------------------------------------------------------------
// Who am I (role decides what the drawer offers; the hub enforces it again)
// ---------------------------------------------------------------------------

let mePromise: Promise<{ id: string; role: string }> | null = null;
/** The signed-in person: GET /api/me when the hub has the auth module, else the owner link. */
export function useMe(): { id: string; role: string } {
  const mode = useMode();
  const hasAuth = useOrgus((s) => !!s.ext.auth);
  const [me, setMe] = useState<{ id: string; role: string }>({ id: 'owner', role: 'owner' });
  useEffect(() => {
    if (mode !== 'live' || !hasAuth) return;
    mePromise ??= apiGet<{ member?: { login?: string; role?: string } }>('/api/me')
      .then((r) => ({ id: r.member?.login ?? 'owner', role: r.member?.role ?? 'owner' }))
      .catch(() => ({ id: 'owner', role: 'owner' }));
    let alive = true;
    void mePromise.then((m) => alive && setMe(m));
    return () => {
      alive = false;
    };
  }, [mode, hasAuth]);
  return me;
}

// ---------------------------------------------------------------------------
// Diff
// ---------------------------------------------------------------------------

export function useDiff(s: Session, scope: DiffScope, commit: string | null, projectId: string | null = null): Loadable<DiffResult> {
  const mode = useMode();
  const [data, setData] = useState<DiffResult | null>(null);
  const [error, setError] = useState<UiError | null>(null);
  const [busy, setBusy] = useState(true);
  const [tick, setTick] = useState(0);
  const key = `${s.id}:${scope}:${commit ?? ''}`;
  const keyRef = useRef(key);
  const working = s.status === 'working';

  useEffect(() => {
    if (keyRef.current !== key) {
      keyRef.current = key;
      setData(null);
    }
    const ctrl = new AbortController();
    setBusy(true);
    setError(null);
    const q = `scope=${scope}${scope === 'commit' && commit ? `&commit=${enc(commit)}` : ''}${scope === 'commit' && projectId ? `&projectId=${enc(projectId)}` : ''}`;
    const load: Promise<DiffResult> =
      mode === 'mock'
        ? new Promise((r) => setTimeout(() => r(demoDiff(s, scope, commit ?? undefined)), 350))
        : api<DiffResult>(`/api/sessions/${enc(s.id)}/diff?${q}`, { signal: ctrl.signal, timeoutMs: 30_000 });
    load
      .then((d) => !ctrl.signal.aborted && setData(d))
      .catch((e) => !ctrl.signal.aborted && setError(toUiError(e, { what: 'Could not load the diff.' })))
      .finally(() => !ctrl.signal.aborted && setBusy(false));
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, mode, tick]);

  useEffect(() => {
    if (!working || mode !== 'live') return;
    const t = setInterval(() => setTick((n) => n + 1), DIFF_REFRESH_MS);
    return () => clearInterval(t);
  }, [working, mode]);

  const reload = useCallback(() => setTick((n) => n + 1), []);
  return { data, loading: busy && !data, refreshing: busy && !!data, error, reload };
}

// ---------------------------------------------------------------------------
// Events
// ---------------------------------------------------------------------------

export interface EventsData {
  events: SessionEvent[];
  partial: boolean;
  omitted: number;
}

export function useSessionEvents(s: Session, live: boolean): Loadable<EventsData> {
  const mode = useMode();
  const [data, setData] = useState<EventsData | null>(null);
  const [error, setError] = useState<UiError | null>(null);
  const [busy, setBusy] = useState(true);
  const [attempt, setAttempt] = useState(0);
  const cursor = useRef(0);
  const epoch = useRef<string | undefined>(undefined);

  useEffect(() => {
    let stop = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    cursor.current = 0;
    epoch.current = undefined;
    setData(null);
    setError(null);
    setBusy(true);
    const once = async (first: boolean) => {
      try {
        const page: SessionEventsPage =
          mode === 'mock'
            ? await new Promise((r) => setTimeout(() => r(demoEvents(s)), 300))
            : await apiGet<SessionEventsPage>(`/api/sessions/${enc(s.id)}/events${eventsQuery(cursor.current, epoch.current)}`);
        if (stop) return;
        const replace = replacesEvents(first, cursor.current, epoch.current, page);
        cursor.current = page.last;
        epoch.current = page.epoch;
        setData((prev) => ({ events: mergeEvents(prev?.events ?? [], page, replace), partial: page.partial, omitted: page.omitted }));
        setError(null);
      } catch (e) {
        if (!stop) setError(toUiError(e, { what: 'Could not load the events.' }));
      } finally {
        if (!stop) setBusy(false);
      }
      if (!stop && live && mode === 'live') timer = setTimeout(() => void once(false), EVENTS_REFRESH_MS);
    };
    void once(true);
    return () => {
      stop = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s.id, mode, live, attempt]);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);
  return { data, loading: busy && !data, refreshing: busy && !!data, error, reload };
}

// ---------------------------------------------------------------------------
// Claims
// ---------------------------------------------------------------------------

export function useClaim(s: Session | undefined): SessionClaim | null {
  const mode = useMode();
  const live = useExt<SessionControlState>('session-control');
  const demo = useDemoControl((st) => (s ? st.claims[s.id] : undefined));
  if (!s) return null;
  if (mode === 'mock') return demo ?? null;
  return live?.claims.find((c) => c.sessionId === s.id) ?? null;
}

// ---------------------------------------------------------------------------
// Messages
// ---------------------------------------------------------------------------

export interface MessagesData {
  messages: SessionMessage[];
  input: SessionInputStatus | null;
  loading: boolean;
  error: UiError | null;
  reload: () => void;
}

export function useMessages(s: Session, enabled: boolean): MessagesData {
  const mode = useMode();
  const liveList = useOrgus(useShallow((st) => (st.ext['session-control'] as SessionControlState | undefined)?.messages[s.id] ?? EMPTY));
  const demoList = useDemoControl((st) => st.messages[s.id] ?? EMPTY);
  const demoClosed = useDemoControl((st) => !!st.closed[s.id]);
  const [fetched, setFetched] = useState<SessionMessagesResponse | null>(null);
  const [error, setError] = useState<UiError | null>(null);
  const [loading, setLoading] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!enabled || mode !== 'live') return;
    let alive = true;
    setLoading(true);
    apiGet<SessionMessagesResponse>(`/api/sessions/${enc(s.id)}/messages`)
      .then((r) => {
        if (!alive) return;
        setFetched(r);
        setError(null);
      })
      .catch((e) => {
        if (!alive) return;
        // A server without the messages route (an older control plane) has no history to show:
        // that is an empty list, not a red error.
        if (e instanceof ApiError && e.status === 404) {
          setFetched(null);
          setError(null);
          return;
        }
        setError(toUiError(e, { what: 'Could not load the messages.', why: 'The server did not return the history.', next: 'Try again in a moment.' }));
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [s.id, enabled, mode, tick, s.status]);

  // The live slice carries only a preview of long messages: fetch the full text of any it
  // shows that the history has not got yet (a message just sent, here or elsewhere).
  const unseen = unseenClipped(fetched?.messages, liveList);
  useEffect(() => {
    if (unseen && enabled && mode === 'live') setTick((n) => n + 1);
  }, [unseen, enabled, mode]);

  useEffect(() => {
    if (mode === 'mock') seedDemoMessages([s]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, s.id]);

  const reload = useCallback(() => setTick((n) => n + 1), []);
  if (mode === 'mock') {
    void demoClosed;
    return { messages: demoList, input: demoInput(s), loading: false, error: null, reload };
  }
  return { messages: mergeMessages(fetched?.messages ?? [], liveList), input: fetched?.input ?? null, loading, error, reload };
}

const EMPTY: SessionMessage[] = [];

export async function sendMessage(s: Session, text: string): Promise<SessionMessage> {
  if (useOrgus.getState().mode === 'mock') return demoSend(s, text);
  try {
    const r = await apiPost<{ message: SessionMessage }>(`/api/sessions/${enc(s.id)}/messages`, { text });
    return r.message;
  } catch (e) {
    if (e instanceof ApiError) throw e;
    throw toUiError(e, { what: 'The message was not sent.' });
  }
}

export async function closeInput(s: Session): Promise<SessionInputStatus> {
  if (useOrgus.getState().mode === 'mock') return demoCloseInput(s);
  const r = await apiPost<{ input: SessionInputStatus }>(`/api/sessions/${enc(s.id)}/input`, { op: 'close' });
  return r.input;
}

/**
 * ADR 0104: the hub holds a summary of a session (no subagent or workflow lists); while the
 * drawer is open, the whole session is fetched from its machine and laid over the live summary,
 * again every DETAIL_REFRESH_MS while it is live. A full session (an older agent) is returned
 * as is. In demo mode nothing is fetched.
 */
const DETAIL_REFRESH_MS = 10_000;
export function useSessionDetail(s: Session | undefined, enabled = true): { session: Session | undefined; detailError: UiError | null } {
  const mode = useMode();
  const summary = !!s && isSummary(s);
  const want = summary && enabled && mode === 'live';
  const [detail, setDetail] = useState<Session | null>(null);
  const [detailError, setDetailError] = useState<UiError | null>(null);
  const id = s?.id;
  const live = !!s && s.status !== 'ended';
  useEffect(() => {
    if (!want || !id) return;
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const load = () => {
      apiGet<{ session: Session }>(`/api/sessions/${enc(id)}/detail`)
        .then((r) => {
          if (!alive) return;
          setDetail(r.session);
          setDetailError(null);
        })
        .catch((e) => alive && setDetailError(toUiError(e, { what: 'Could not load the session detail.' })))
        .finally(() => {
          if (alive && live) timer = setTimeout(load, DETAIL_REFRESH_MS);
        });
    };
    load();
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
  }, [want, id, live]);
  const session = useMemo(() => {
    if (!s || !summary || !detail || detail.id !== s.id) return s;
    // The live summary leads (status, counters); the detail fills what it left out.
    const { summary: _flag, running: _running, ...ext } = { ...(detail.ext ?? {}), ...(s.ext ?? {}) } as Record<string, unknown>;
    return { ...detail, ...s, subagents: detail.subagents, workflows: detail.workflows, lastText: detail.lastText || s.lastText, ext };
  }, [s, summary, detail]);
  return { session, detailError };
}
